import React, { useEffect, useId, useRef, useState } from "react";
import classNames from "classnames";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

import { TestProps } from "shared/types";

import Button, { ButtonProps } from "features/components/Button";
import Menu from "features/components/Menu";
import type { PopoverOrigin } from "features/components/Popover";
import Tooltip from "features/components/Tooltip";
import { getFilterMenuWrapperClass } from "features/ui/Filters/FilterBuilder/utils";
import { Option, SelectOption } from "features/ui/Select";

export const OPTION_TOOLTIP_MAX_WIDTH_PX = 900;

export interface TriggerProps {
  id: string;
  "aria-haspopup": "true";
  "aria-controls"?: string;
  "aria-expanded"?: "true";
}

export interface DropdownSelectOption<T = Option>
  extends SelectOption<T>, TestProps {
  href?: string;
  download?: string;
  onClick?: () => void;
  classNames?: string;
  delimiterBelow?: boolean;
  tooltipContent?: React.ReactNode;
}

interface DropdownSelectProps<
  T extends DropdownSelectOption,
> extends TestProps {
  open?: boolean;
  label?: string | React.ReactNode;
  size?: ButtonProps["size"];
  startIcon?: React.ReactElement;
  icon?: React.ReactElement | null;
  ariaLabel?: string;
  options?: T[]; // specify either options or content - options override the content
  content?: React.ReactNode; // options override the content
  // Renders a trigger of the caller's own, given the props that say which menu it opens. They belong on the element
  // that takes the focus — for a composite like TextField, that is its input rather than the root around it.
  renderTrigger?: (props: TriggerProps) => React.ReactNode;
  onOpen?: (isOpen: boolean) => void;
  onSelect?: (value: T) => void;
  color?: ButtonProps["color"];
  buttonClass?: string;
  hasAdvancedFilters?: boolean;
  variant?: "text" | "contained";
  disabled?: ButtonProps["disabled"];
  disabledOnClick?: boolean;
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
  tooltip?: React.ReactElement;
  autoWidth?: boolean;
  className?: string;
}

const DropdownSelect = <T extends DropdownSelectOption>({
  open,
  label,
  ariaLabel,
  startIcon,
  icon = <ArrowDropDownIcon />,
  options,
  content,
  renderTrigger,
  onOpen,
  onSelect,
  color = "secondary",
  variant = "contained",
  buttonClass = "dropdown-select-button",
  testId,
  hasAdvancedFilters,
  disabled,
  disabledOnClick,
  tooltip,
  autoWidth = false,
  size = "medium",
  className,
  anchorOrigin = {
    vertical: "bottom",
    horizontal: "left",
  },
  transformOrigin = {
    vertical: "top",
    horizontal: "left",
  },
}: DropdownSelectProps<T>) => {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const showContent = Boolean(!options && content);
  const triggerRef = useRef<HTMLDivElement>(null);
  const [menuWidth, setMenuWidth] = useState<number | undefined>(undefined);
  // The anchor is what the menu opens against, so it is also what says whether it is open. Several of these can
  // share a page — one per table row — so the ids they point at each other with have to be their own.
  const isOpen = Boolean(anchorEl);
  const instanceId = useId();
  const menuId = `dropdown-menu-${instanceId}`;
  const triggerId = `dropdown-trigger-${instanceId}`;

  const openMenu = (anchor: HTMLDivElement) => {
    if (disabledOnClick) {
      return;
    }

    // Calculate the width of the trigger element
    if (autoWidth && triggerRef.current) {
      setMenuWidth(triggerRef.current.offsetWidth);
    }

    setAnchorEl(anchor);
    onOpen && onOpen(true);
  };

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) =>
    openMenu(event.currentTarget);

  // A custom trigger can be a composite — a read-only TextField standing in for a select — whose focusable element
  // sits inside it and does not turn a key press into a click of its own. The keys that open a menu are answered
  // here, where every trigger's events pass through.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (isOpen || (event.key !== "Enter" && event.key !== " ")) {
      return;
    }

    event.preventDefault();
    openMenu(event.currentTarget);
  };

  const handleCloseCallback = (reason: string) => {
    if (reason === "tabKeyDown") {
      return;
    }

    handleClose();
  };

  const handleClose = () => {
    onOpen && onOpen(false);
    setAnchorEl(null);
  };

  // use "open" prop to forcefully "close" the dropdown
  // we can't forcefully open it as we might not have anchorEl ready (if open was a default on 1st render)
  useEffect(() => {
    if (open === false) {
      handleClose();
    } else if (open === true && !anchorEl) {
      // If we want to open but don't have an anchor, use the trigger ref
      setAnchorEl(triggerRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, anchorEl]);

  // What the trigger has to say for itself: which menu it opens, and whether that menu is open right now.
  const popupProps: TriggerProps = {
    id: triggerId,
    "aria-haspopup": "true",
    "aria-controls": isOpen ? menuId : undefined,
    "aria-expanded": isOpen ? "true" : undefined,
  };

  const trigger = renderTrigger ? (
    renderTrigger(popupProps)
  ) : (
    <Button
      {...popupProps}
      color={color || "secondary"}
      className={classNames(
        "dropdown-select-button !text-sm !font-normal !pl-3",
        buttonClass
      )}
      variant={variant}
      startIcon={startIcon}
      endIcon={icon}
      size={size}
      disabled={disabled}
      aria-label={ariaLabel}
    >
      {label}
    </Button>
  );

  const CTA = (
    <div
      ref={triggerRef}
      data-testid={testId}
      onClick={disabled ? undefined : handleClick}
      onKeyDown={disabled ? undefined : handleKeyDown}
      className={classNames("inline-block whitespace-nowrap", {
        "pointer-events-none opacity-50": renderTrigger && disabled,
        "w-full!": autoWidth,
      })}
    >
      {tooltip ? (
        <Tooltip
          title={tooltip}
          placement="bottom"
          enterDelay={500}
          leaveDelay={0}
        >
          <div>{trigger}</div>
        </Tooltip>
      ) : (
        trigger
      )}
    </div>
  );

  const applyCustomClasses = hasAdvancedFilters !== undefined;

  return (
    <div className={className}>
      {CTA}
      <Menu
        id={menuId}
        open={Boolean(anchorEl)}
        onClose={(_: Object, reason: string) => handleCloseCallback(reason)}
        anchorEl={anchorEl}
        marginThreshold={0}
        className={
          applyCustomClasses
            ? getFilterMenuWrapperClass(hasAdvancedFilters)
            : undefined
        }
        slotProps={
          applyCustomClasses
            ? {
                paper: {
                  sx: {
                    minWidth: menuWidth ? `${menuWidth}px` : "fit-content",
                    maxWidth: "100%",
                    padding: 2,
                  },
                },
                list: {
                  sx: {
                    py: 0,
                    zIndex: 999,
                  },

                  "aria-labelledby": triggerId,
                },
              }
            : {
                paper: {
                  sx: {
                    minWidth: menuWidth ? `${menuWidth}px` : "fit-content",
                  },
                },
                list: {
                  sx: {
                    py: 0,
                    zIndex: 999,
                  },
                  "aria-labelledby": triggerId,
                },
              }
        }
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        // needed so FilterSelector properly takes full-height when put inside the content
        transitionDuration={0}
        disableRestoreFocus
        disableScrollLock
        container={document.body}
      >
        <div
          className={classNames("flex flex-col text-sm! max-w-full", {
            "max-h-80! overflow-y-auto": options,
          })}
        >
          {showContent && (
            <div
              onKeyDown={(event: any) => {
                // needed so that Menu doesn't default to "go-to MenuItem logic" when we type ..
                event.stopPropagation();
              }}
            >
              {content}
            </div>
          )}
          {options &&
            options.map((option) => {
              const optionButton = (
                <Button
                  variant="text"
                  onClick={(e) => {
                    e.stopPropagation();
                    // close popover before calling onSelect so it acts more like a dropdown select
                    handleClose();
                    onSelect && onSelect(option);
                  }}
                  href={option.href}
                  fullWidth
                  className={classNames(
                    "justify-start! px-4! py-2! text-right !w-full !text-base !text-gray-700 !font-normal hover:!bg-slate-100",
                    option.classNames
                  )}
                  size={size}
                  testId={option.testId}
                  download={option.download}
                >
                  {option.value}
                </Button>
              );

              return (
                <React.Fragment key={option.id}>
                  {option.tooltipContent ? (
                    <Tooltip
                      title={option.tooltipContent}
                      placement="right"
                      enterDelay={300}
                      leaveDelay={0}
                      disableFocusListener
                      slotProps={{
                        tooltip: {
                          sx: { maxWidth: OPTION_TOOLTIP_MAX_WIDTH_PX },
                        },
                      }}
                    >
                      <div>{optionButton}</div>
                    </Tooltip>
                  ) : (
                    optionButton
                  )}
                  {option.delimiterBelow && (
                    <hr className="text-gray-200 mx-2 my-1" />
                  )}
                </React.Fragment>
              );
            })}
        </div>
      </Menu>
    </div>
  );
};

export default DropdownSelect;
